import { cellIndex, isIntegerCoordinate } from "./geometry";
import {
  coordinate,
  fieldCell,
  freezeGameState,
  freezeOutcome,
  opponentOf,
  removedCell,
  type Cell,
  type GameOutcome,
  type GameState,
  type Move,
  type Player,
  type Transition,
  type TransitionEvent,
} from "./model";
import { moveNotation } from "./notation";
import {
  createInitialCells,
  findOrphans,
  generateLegacyLegalMovesUnchecked,
  generateLegalMovesUnchecked,
  generateVersion2LegalMovesUnchecked,
  generateVersion3LegalMovesUnchecked,
} from "./rules";
import { createGameState, validateGameState, type StateValidationIssue } from "./validation";

export type MoveApplicationError =
  | Readonly<{
      code: "invalid-state";
      message: string;
      issues: readonly StateValidationIssue[];
    }>
  | Readonly<{
      code: "game-over" | "malformed-move" | "illegal-move";
      message: string;
    }>;

export type MoveApplicationResult =
  | Readonly<{ ok: true; transition: Transition }>
  | Readonly<{ ok: false; error: MoveApplicationError }>;

type MutableFieldCell = { kind: "field"; stack: Player[] };
type MutableCell = { kind: "removed" } | MutableFieldCell;

function cloneCells(cells: readonly Cell[]): MutableCell[] {
  return cells.map((cell) =>
    cell.kind === "removed" ? { kind: "removed" } : { kind: "field", stack: [...cell.stack] },
  );
}

function hasExactMoveShape(value: unknown): value is Move {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  if (
    (record["kind"] !== "normal" && record["kind"] !== "passage") ||
    !isIntegerCoordinate(record["from"]) ||
    !isIntegerCoordinate(record["to"])
  ) {
    return false;
  }
  const keys = Object.keys(record).sort();
  return keys.length === 3 && keys[0] === "from" && keys[1] === "kind" && keys[2] === "to";
}

function freezeEvent(event: TransitionEvent): TransitionEvent {
  if (event.kind === "orphan-cleanup") {
    return Object.freeze({
      ...event,
      fields: Object.freeze(
        event.fields.map((field) =>
          Object.freeze({
            position: coordinate(field.position.row, field.position.column),
            stack: Object.freeze([...field.stack]),
          }),
        ),
      ),
    });
  }
  if (event.kind === "game-ended") {
    return Object.freeze({ ...event, outcome: freezeOutcome(event.outcome) });
  }
  return Object.freeze({ ...event });
}

function finish(state: GameState, events: readonly TransitionEvent[]): Transition {
  return Object.freeze({
    state,
    events: Object.freeze(events.map(freezeEvent)),
  });
}

function success(transition: Transition): MoveApplicationResult {
  return Object.freeze({
    ok: true,
    transition,
  });
}

function removeSourceAndOrphans(
  cells: MutableCell[],
  from: Move["from"],
  events: TransitionEvent[],
): void {
  cells[cellIndex(from)] = { kind: "removed" };
  events.push({ kind: "field-removed", position: from, reason: "emptied-source" });

  const orphanSet = findOrphans(cells);
  if (orphanSet.length === 0) return;

  for (const orphan of orphanSet) cells[cellIndex(orphan.position)] = { kind: "removed" };
  events.push({ kind: "orphan-cleanup", fields: orphanSet });
}

/**
 * Internal transition core. The caller must provide a canonical engine state and
 * a move returned by legal-move generation for that exact state.
 */
type LegalMoveGenerator = (state: GameState) => readonly Move[];

function applyKnownLegalMove(
  state: GameState,
  legalMove: Move,
  generateMoves: LegalMoveGenerator,
): Transition {
  const cells = cloneCells(state.cells);
  const source = cells[cellIndex(legalMove.from)];
  if (source?.kind !== "field") {
    throw new Error("Legal-move generation produced a missing source field.");
  }
  const movingDisc = source.stack.pop();
  if (movingDisc !== state.activePlayer) {
    throw new Error("Legal-move generation produced a source not controlled by the active player.");
  }

  const events: TransitionEvent[] = [];
  if (legalMove.kind === "passage") {
    events.push({
      kind: "disc-exited",
      player: movingDisc,
      from: legalMove.from,
      to: legalMove.to,
    });
    if (source.stack.length === 0) removeSourceAndOrphans(cells, legalMove.from, events);

    const outcome: GameOutcome = Object.freeze({ kind: "passage", winner: movingDisc });
    const nextState = freezeGameState({
      cells: cells.map((cell) => (cell.kind === "removed" ? removedCell() : fieldCell(cell.stack))),
      activePlayer: state.activePlayer,
      outcome,
    });
    events.push({ kind: "game-ended", outcome });
    return finish(nextState, events);
  }

  const destination = cells[cellIndex(legalMove.to)];
  if (destination?.kind !== "field") {
    throw new Error("Legal-move generation produced a missing destination field.");
  }
  destination.stack.push(movingDisc);
  events.push({
    kind: "disc-moved",
    player: movingDisc,
    from: legalMove.from,
    to: legalMove.to,
  });
  if (source.stack.length === 0) removeSourceAndOrphans(cells, legalMove.from, events);

  const nextPlayer = opponentOf(state.activePlayer);
  const ruleState = freezeGameState({
    cells: cells.map((cell) => (cell.kind === "removed" ? removedCell() : fieldCell(cell.stack))),
    activePlayer: nextPlayer,
    outcome: null,
  });
  const outcome: GameOutcome | null =
    generateMoves(ruleState).length === 0
      ? Object.freeze({
          kind: "no-moves",
          winner: state.activePlayer,
          loser: nextPlayer,
        })
      : null;
  const nextState = freezeGameState({ ...ruleState, outcome });

  events.push({ kind: "turn-changed", from: state.activePlayer, to: nextPlayer });
  if (outcome !== null) events.push({ kind: "game-ended", outcome });
  return finish(nextState, events);
}

export function applyCanonicalLegalMove(state: GameState, legalMove: Move): Transition {
  return applyKnownLegalMove(state, legalMove, generateLegalMovesUnchecked);
}

/** Applies a trusted rules-version 1 move from an active or completed recording. */
export function applyLegacyRecordedMove(state: GameState, move: Move): Transition | null {
  const legalMove = generateLegacyLegalMovesUnchecked(state).find(
    (candidate) => moveNotation(candidate) === moveNotation(move),
  );
  return legalMove === undefined
    ? null
    : applyKnownLegalMove(state, legalMove, generateLegacyLegalMovesUnchecked);
}

/** Applies a trusted rules-version 2 move using the semantics shipped with that version. */
export function applyVersion2RecordedMove(state: GameState, move: Move): Transition | null {
  const legalMove = generateVersion2LegalMovesUnchecked(state).find(
    (candidate) => moveNotation(candidate) === moveNotation(move),
  );
  return legalMove === undefined
    ? null
    : applyKnownLegalMove(state, legalMove, generateVersion2LegalMovesUnchecked);
}

/** Applies a trusted rules-version 3 move using the semantics shipped with that version. */
export function applyVersion3RecordedMove(state: GameState, move: Move): Transition | null {
  const legalMove = generateVersion3LegalMovesUnchecked(state).find(
    (candidate) => moveNotation(candidate) === moveNotation(move),
  );
  return legalMove === undefined
    ? null
    : applyKnownLegalMove(state, legalMove, generateVersion3LegalMovesUnchecked);
}

export function createInitialGame(): GameState {
  return createGameState({
    cells: createInitialCells(),
    activePlayer: "black",
    outcome: null,
  });
}

export function applyMove(state: GameState, move: Move): MoveApplicationResult {
  const stateValidation = validateGameState(state);
  if (!stateValidation.ok) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({
        code: "invalid-state",
        message: "Cannot apply a move to an invalid game state.",
        issues: stateValidation.issues,
      }),
    });
  }
  const canonicalState = stateValidation.state;

  if (canonicalState.outcome !== null) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({ code: "game-over", message: "The game is already over." }),
    });
  }
  if (!hasExactMoveShape(move)) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({ code: "malformed-move", message: "The move shape is invalid." }),
    });
  }

  const legalMove = generateLegalMovesUnchecked(canonicalState).find(
    (candidate) => moveNotation(candidate) === moveNotation(move),
  );
  if (legalMove === undefined) {
    return Object.freeze({
      ok: false,
      error: Object.freeze({ code: "illegal-move", message: "The move is not legal." }),
    });
  }

  return success(applyCanonicalLegalMove(canonicalState, legalMove));
}
